<template>
  <div>
    <ClinicPageHeader
      :title="`Agenda · ${doctor.name || ''}`"
      subtitle="Visão da clínica sobre a agenda do profissional de saúde"
    >
      <template #actions>
        <router-link
          :to="{ name: 'ClinicSchedule' }"
          class="btn btn--outline btn--sm"
        >
          Agenda geral
        </router-link>
        <!-- Proto usa btn--accent; regra do módulo troca CTA para btn--primary -->
        <button class="btn btn--primary btn--sm" @click="newFit">
          + Encaixe
        </button>
      </template>
    </ClinicPageHeader>

    <div class="dbody">
      <!-- Erro -->
      <ClinicStateBox
        v-if="state === 'error'"
        state="error"
        title="Não foi possível carregar a agenda"
        message="Ocorreu um erro ao buscar a agenda do profissional de saúde. Tente novamente."
      >
        <template #actions>
          <button class="btn btn--primary btn--sm" @click="reload">
            Tentar de novo
          </button>
        </template>
      </ClinicStateBox>

      <!-- Loading -->
      <template v-else-if="state === 'loading'">
        <div class="card" style="margin-bottom:var(--space-5)">
          <div class="skel-line" style="width:40%;height:24px" />
        </div>
        <div class="card">
          <div class="skel-line" style="width:100%;height:360px" />
        </div>
      </template>

      <!-- Conteúdo -->
      <template v-else>
        <!-- Cabeçalho do médico -->
        <div
          class="card"
          style="display:flex;flex-direction:row;align-items:center;gap:var(--space-4);margin-bottom:var(--space-5)"
        >
          <div class="avatar avatar--md" :class="doctor.avatarClass">
            {{ doctor.initials }}
          </div>
          <div class="stack" style="gap:1px;flex:1">
            <span
              style="font-weight:var(--fw-bold);color:var(--color-gray-900)"
            >
              {{ doctor.name }}
            </span>
            <span
              style="font-size:var(--font-size-sm);color:var(--color-gray-500)"
            >
              {{ doctor.specialty }} · {{ doctor.crm }} · ocupação
              {{ doctor.occupation }}% esta semana
            </span>
          </div>
          <select
            v-model.number="selectedDoctorId"
            class="select"
            style="width:auto;height:40px"
            @change="onDoctorChange"
          >
            <option v-for="d in doctors" :key="d.id" :value="d.id">
              {{ d.name }}
            </option>
          </select>
        </div>

        <!-- Navegação de semana -->
        <div class="agenda-head">
          <div class="agenda-nav">
            <button
              :disabled="weekIndex === 0"
              aria-label="Semana anterior"
              @click="prevWeek"
            >
              ‹
            </button>
            <span class="today">{{ currentWeek.label }}</span>
            <button
              :disabled="weekIndex >= weeks.length - 1"
              aria-label="Próxima semana"
              @click="nextWeek"
            >
              ›
            </button>
          </div>
          <div class="row" style="gap:var(--space-2)">
            <button class="btn btn--ghost btn--sm" @click="today">Hoje</button>
            <router-link
              :to="{ name: 'ClinicSchedule' }"
              class="btn btn--outline btn--sm"
            >
              Agenda geral
            </router-link>
          </div>
        </div>

        <!-- Grade semanal -->
        <template v-if="hasAppointments">
          <div class="agenda" :style="{ '--cols': currentWeek.days.length }">
            <div class="col-h corner" />
            <div v-for="d in currentWeek.days" :key="d.nm" class="col-h">
              <div class="stack" style="gap:0">
                <span class="nm">{{ d.nm }}</span>
                <span class="sp">{{ d.date }}</span>
              </div>
            </div>

            <template v-for="row in currentWeek.grid">
              <div :key="`t-${row.time}`" class="time">{{ row.time }}</div>
              <div
                v-for="(cell, ci) in row.cells"
                :key="`c-${row.time}-${ci}`"
                class="cell"
              >
                <div
                  v-if="cell"
                  class="appt-block"
                  :class="`is-${cell.status}`"
                  @click="openAppt(cell)"
                >
                  <b>{{ cell.name }}</b>
                  <span>{{ cell.type }}</span>
                </div>
              </div>
            </template>
          </div>

          <div class="legend">
            <div class="lg">
              <i style="background:var(--color-primary)" />
              Confirmada
            </div>
            <div class="lg">
              <i style="background:var(--color-warning)" />
              A confirmar
            </div>
            <div class="lg">
              <i style="background:var(--color-success)" />
              Realizada
            </div>
            <div class="lg">
              <i style="background:var(--color-gray-300)" />
              Bloqueio
            </div>
          </div>
        </template>

        <!-- Semana vazia -->
        <ClinicStateBox
          v-else
          state="empty"
          title="Nenhuma consulta nesta semana"
          message="Não há consultas agendadas para este profissional de saúde no período selecionado."
        />
      </template>
    </div>
  </div>
</template>

<script>
import ClinicPageHeader from "@/components/clinica/ClinicPageHeader.vue";
import ClinicStateBox from "@/components/clinica/ClinicStateBox.vue";
import { ClinicAgenda, ClinicDoctors } from "@/services/clinica";

// Colunas da grade: dias úteis (Seg → Sex), como no handoff.
const DOW = ["SEG", "TER", "QUA", "QUI", "SEX"];

// Status de consulta (API) → status visual do bloco (.appt-block.is-*).
const STATUS_MAP = {
  confirmada: "confirmed",
  realizada: "done",
  agendada: "pending",
  aguardando_pgto: "pending",
  cancelada: "blocked"
};

const pad = n => String(n).padStart(2, "0");
const timeOf = dt => (dt.split("T")[1] || "").slice(0, 5);
// Índice do dia útil (Seg = 0 … Sex = 4). Sáb/Dom ficam >= 5.
const weekdayIndex = dt => (new Date(dt).getDay() + 6) % 7;
const fmtDM = d => `${pad(d.getDate())}/${pad(d.getMonth() + 1)}`;

function mondayOf(dt) {
  const d = new Date(dt);
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  d.setHours(0, 0, 0, 0);
  return d;
}

function weekLabel(monday) {
  const fri = new Date(monday);
  fri.setDate(monday.getDate() + 4);
  const month = monday.toLocaleDateString("pt-BR", { month: "long" });
  return `${monday.getDate()}–${fri.getDate()} de ${month} de ${monday.getFullYear()}`;
}

function buildWeek(monday, appts) {
  const days = DOW.map((nm, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return { nm, date: fmtDM(d) };
  });
  const times = Array.from(new Set(appts.map(a => timeOf(a.datetime)))).sort();
  const grid = times.map(time => {
    const cells = DOW.map((_, dayIdx) => {
      const a = appts.find(
        x => weekdayIndex(x.datetime) === dayIdx && timeOf(x.datetime) === time
      );
      if (!a) return null;
      return {
        patientId: a.patientId,
        name: a.patientName || "—",
        type: a.type || "Consulta",
        status: STATUS_MAP[a.status] || "pending"
      };
    });
    return { time, cells };
  });
  return { label: weekLabel(monday), days, grid };
}

// Agrupa as consultas por semana (segunda-feira) e monta uma grade por semana.
function buildWeeks(appts) {
  const groups = {};
  appts
    .filter(a => weekdayIndex(a.datetime) < 5)
    .forEach(a => {
      const key = mondayOf(a.datetime).getTime();
      if (!groups[key])
        groups[key] = { monday: mondayOf(a.datetime), appts: [] };
      groups[key].appts.push(a);
    });
  return Object.keys(groups)
    .map(Number)
    .sort((x, y) => x - y)
    .map(k => buildWeek(groups[k].monday, groups[k].appts));
}

export default {
  name: "ClinicDoctorSchedule",
  components: { ClinicPageHeader, ClinicStateBox },
  data() {
    return {
      // loading → ready/error. Preview de estados via ?state= na URL.
      state: "loading",
      doctors: [], // lista real (seletor) — antes vinha do stub @/data/clinica
      doctor: { name: "" },
      selectedDoctorId: this.$route.params.id,
      weekIndex: 0,
      weeks: []
    };
  },
  created() {
    this.loadDoctors();
    this.fetch();
  },
  methods: {
    async loadDoctors() {
      // Lista real de médicos p/ o seletor (antes: stub).
      try {
        const { data } = await ClinicDoctors.list();
        this.doctors = data;
        if (!this.selectedDoctorId && data[0])
          this.selectedDoctorId = data[0].id;
      } catch (e) {
        this.doctors = [];
      }
    },
    async fetch(id = this.selectedDoctorId) {
      this.state = "loading";
      try {
        // agenda (grade) + médico completo (header: iniciais/avatar/especialidade/
        // CRM/ocupação — o endpoint de agenda só devolve {id,name}).
        const [agRes, docRes] = await Promise.all([
          ClinicAgenda.forDoctor(id, {
            fail: this.$route.query.state === "error"
          }),
          ClinicDoctors.get(id)
        ]);
        this.doctor = { ...agRes.data.doctor, ...docRes.data };
        this.weeks = buildWeeks(agRes.data.appointments || []);
        this.weekIndex = 0;
        this.state = "ready";
      } catch (e) {
        this.state = "error";
      }
    },
    reload() {
      this.fetch();
    },
    onDoctorChange() {
      // Recarrega a agenda do médico selecionado no seletor.
      this.fetch(this.selectedDoctorId);
    },
    prevWeek() {
      if (this.weekIndex > 0) this.weekIndex -= 1;
    },
    nextWeek() {
      if (this.weekIndex < this.weeks.length - 1) this.weekIndex += 1;
    },
    today() {
      this.weekIndex = 0;
    },
    newFit() {
      // Fase 2: abre modal de encaixe manual na agenda.
      this.$router.push({ name: "ClinicSchedule" });
    },
    openAppt(cell) {
      // Abre a ficha do paciente da consulta clicada.
      this.$router.push({
        name: "ClinicPatientProfile",
        params: { id: String(cell.patientId) }
      });
    }
  },
  computed: {
    currentWeek() {
      return this.weeks[this.weekIndex] || { label: "", days: [], grid: [] };
    },
    hasAppointments() {
      return this.currentWeek.grid.some(row =>
        row.cells.some(cell => cell != null)
      );
    }
  }
};
</script>
